import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    PlusIcon,
    PencilIcon,
    TrashIcon,
    XMarkIcon,
    PhotoIcon
} from '@heroicons/react/24/outline';
import { useFirestore } from '../../hooks/useFirestore';
import { getClients } from '../../firebase/firestore';
import {
    collection,
    addDoc,
    updateDoc,
    deleteDoc,
    doc
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import Button from '../../components/common/Button';
import Loading from '../../components/common/Loading';
import './ManageClients.css';

const ManageClients = () => {
    const { data: clients, loading, refresh } = useFirestore(useCallback(() => getClients(), []));
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        logo: '',
        order: 1
    });

    const resetForm = () => {
        setFormData({
            name: '',
            logo: '',
            order: clients ? clients.length + 1 : 1
        });
        setEditingClient(null);
    };

    const handleOpenModal = (client = null) => {
        if (client) {
            setEditingClient(client);
            setFormData({
                name: client.name,
                logo: client.logo,
                order: client.order
            });
        } else {
            resetForm();
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingClient) {
                await updateDoc(doc(db, 'clients', editingClient.id), formData);
            } else {
                await addDoc(collection(db, 'clients'), formData);
            }
            setIsModalOpen(false);
            refresh();
        } catch (error) {
            console.error('Error saving client:', error);
            alert('فشل حفظ بيانات العميل.');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('هل أنت متأكد من حذف هذا العميل؟')) {
            try {
                await deleteDoc(doc(db, 'clients', id));
                refresh();
            } catch (error) {
                console.error('Error deleting client:', error);
                if (error.code === 'permission-denied') {
                    alert('خطأ في الصلاحيات: يرجى تسجيل الخروج ثم الدخول مرة أخرى.');
                } else {
                    alert('فشل حذف العميل.');
                }
            }
        }
    };

    if (loading && !clients) return <Loading />;

    return (
        <div className="manage-clients">
            <div className="admin-page-header">
                <h1>إدارة العملاء</h1>
                <Button variant="primary" onClick={() => handleOpenModal()}>
                    <PlusIcon className="btn-icon" />
                    <span>إضافة عميل</span>
                </Button>
            </div>

            <div className="clients-grid">
                {clients?.map((client) => (
                    <motion.div
                        key={client.id}
                        className="client-admin-card shadow-premium"
                        layout
                    >
                        <div className="client-logo-preview">
                            {client.logo ? (
                                <img src={client.logo} alt={client.name} />
                            ) : (
                                <PhotoIcon className="placeholder-icon" />
                            )}
                        </div>
                        <div className="client-admin-info">
                            <h3>{client.name}</h3>
                            <p>الترتيب: {client.order}</p>
                        </div>
                        <div className="client-admin-actions">
                            <button className="action-btn edit" onClick={() => handleOpenModal(client)}>
                                <PencilIcon />
                            </button>
                            <button className="action-btn delete" onClick={() => handleDelete(client.id)}>
                                <TrashIcon />
                            </button>
                        </div>
                    </motion.div>
                ))}
            </div>

            <AnimatePresence>
                {isModalOpen && (
                    <motion.div
                        className="modal-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            className="modal-content"
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                        >
                            <div className="modal-header">
                                <h2>{editingClient ? 'تعديل بيانات العميل' : 'إضافة عميل جديد'}</h2>
                                <button className="close-btn" onClick={() => setIsModalOpen(false)}>
                                    <XMarkIcon />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="admin-form">
                                <div className="form-group">
                                    <label>اسم العميل</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="مثال: شركة النصر"
                                    />
                                </div>

                                <div className="form-group">
                                    <label>رابط الشعار (URL)</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.logo}
                                        onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                                        placeholder="https://example.com/logo.png"
                                    />
                                    <small>يرجى رفع الشعار على مركز رفع صور ووضع الرابط هنا</small>
                                </div>

                                <div className="form-group">
                                    <label>الترتيب</label>
                                    <input
                                        type="number"
                                        value={formData.order}
                                        onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                                    />
                                </div>

                                <div className="modal-footer">
                                    <Button type="button" variant="ghost" onClick={() => setIsModalOpen(false)}>
                                        إلغاء
                                    </Button>
                                    <Button type="submit" variant="primary">
                                        {editingClient ? 'تحديث' : 'إضافة'}
                                    </Button>
                                </div>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ManageClients;
